import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface CompletedMatch {
  id: string;
  match_number: string;
  start_date: string;
  end_date: string;
  total_amount: number;
  hourly_rate: number;
  daily_hours: number;
  completed_at: string;
  requester_id: string;
  receiver_id: string;
}

export interface MonthlyStats {
  month: string;
  count: number;
  totalAmount: number;
  totalFee: number;
}

const FEE_RATE = 0.05;

export function useDashboardStats() {
  const [completedMatches, setCompletedMatches] = useState<CompletedMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('match_requests')
        .select('*')
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (err) throw err;
      setCompletedMatches((data || []) as CompletedMatch[]);
    } catch (e: any) {
      setError(e.message || '데이터 로딩 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const totalCompleted = completedMatches.length;
  const totalAmount = completedMatches.reduce((s, m) => s + (m.total_amount || 0), 0);
  const totalFee = Math.round(totalAmount * FEE_RATE);
  const netAmount = totalAmount - totalFee;

  const monthlyStats: MonthlyStats[] = (() => {
    const map = new Map<string, MonthlyStats>();
    completedMatches.forEach((m) => {
      if (!m.completed_at) return;
      const month = m.completed_at.slice(0, 7);
      const existing = map.get(month) || { month, count: 0, totalAmount: 0, totalFee: 0 };
      existing.count += 1;
      existing.totalAmount += m.total_amount || 0;
      existing.totalFee = Math.round(existing.totalAmount * FEE_RATE);
      map.set(month, existing);
    });
    return [...map.values()].sort((a, b) => b.month.localeCompare(a.month));
  })();

  const avgAmount = totalCompleted > 0 ? Math.round(totalAmount / totalCompleted) : 0;
  const avgFee = totalCompleted > 0 ? Math.round(totalFee / totalCompleted) : 0;

  return {
    completedMatches,
    totalCompleted,
    totalAmount,
    totalFee,
    netAmount,
    monthlyStats,
    avgAmount,
    avgFee,
    loading,
    error,
    refetch: fetch,
  };
}